import { useEffect, useState, useRef } from 'react';

import { TWalletType, useWalletType } from '@entities/wallets';
import { useFilter } from '@features/table-filter';
import { useMerchantTestMode } from '@features/test-mode-toggler';

import { useGetWalletsQuery, useGetWalletsDebugQuery, useGetWalletsClickhouseQuery } from '../api';
import type { IWalletFilter } from '../model/types';

interface IProps {
  type?: TWalletType;
  refreshInterval: string;
  isClickhouse?: boolean;
}

export const useWallets = ({ type, refreshInterval, isClickhouse }: IProps) => {
  const filter = useFilter<IWalletFilter>();
  const { currentFilter, updateFilter, resetFilter } = filter;
  const isDebugMode = currentFilter.get('debug_mode') === '1';

  const currentWalletType = useWalletType();
  const hasSameType = currentWalletType === type;

  // Track type changes to show loading spinner when type changes
  // this gives table time to rebuild columns
  const [isTypeChanging, setIsTypeChanging] = useState(false);
  const previousTypeRef = useRef<TWalletType | undefined>(type);
  const hasAppliedDefaultStatusRef = useRef(false);

  // Always set this parameters
  // This prevents limit from being shown in the wallets table, remove after fix on backend is done
  if (!currentFilter.get('without_settings')) currentFilter.set('without_settings', '1');
  if (!currentFilter.get('with_relations')) currentFilter.set('with_relations', '1');

  // Make sure the wallet type is set before any request is made
  if (!currentFilter.get('filter[type]')) {
    updateFilter({ 'filter[onlyParents]': 'true', 'filter[type]': type });
  }

  useEffect(() => {
    if (isClickhouse) {
      if (type !== 'deposit') {
        hasAppliedDefaultStatusRef.current = false;
        return;
      }

      const hasStatusFilter = Boolean(currentFilter.get('filter[status]'));
      if (hasStatusFilter) {
        hasAppliedDefaultStatusRef.current = true;
        return;
      }

      if (!hasAppliedDefaultStatusRef.current) {
        updateFilter({ 'filter[status]': 'live' });
        hasAppliedDefaultStatusRef.current = true;
      }
    }
  }, [type, currentFilter, updateFilter, isClickhouse]);

  const midFilter = currentFilter.get('filter[mid]');
  if (midFilter) {
    currentFilter.delete('filter[onlyParents]');
  } else {
    currentFilter.set('filter[onlyParents]', 'true');
  }

  const queryOptions = {
    pollingInterval: refreshInterval === 'off' ? undefined : parseInt(refreshInterval),
    skip: false,
  };

  const clickhouseQuery = useGetWalletsClickhouseQuery(currentFilter.toString(), {
    ...queryOptions,
    skip: !isClickhouse,
  });

  const regularQuery = useGetWalletsQuery(currentFilter.toString(), {
    ...queryOptions,
    skip: isDebugMode || isClickhouse,
  });

  const debugQuery = useGetWalletsDebugQuery(currentFilter.toString(), {
    ...queryOptions,
    skip: !isDebugMode,
  });

  useMerchantTestMode({ currentFilter, updateFilter });

  const {
    data: wallets,
    isLoading,
    isError,
    error,
    isFetching,
    refetch,
  } = isClickhouse ? clickhouseQuery : isDebugMode ? debugQuery : regularQuery;

  useEffect(() => {
    // Check if type has actually changed
    if (previousTypeRef.current !== type) {
      setIsTypeChanging(true);
      previousTypeRef.current = type;
    }

    if (!hasSameType) {
      resetFilter();
      updateFilter({ 'filter[onlyParents]': 'true', 'filter[type]': type });
    }
  }, [type]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset type changing state when we have new data
  useEffect(() => {
    if (isTypeChanging && wallets) {
      setIsTypeChanging(false);
    }
  }, [wallets, isTypeChanging]);

  return {
    wallets,
    isError,
    error,
    isLoading: isLoading || isTypeChanging, // Show loading when initially loading OR when type is changing
    isFetching,
    refetch,

    filter,
  };
};
